import { useMemo, useEffect } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { EnabledDisabled } from 'modules/Settings/constant';
import {
  GroupType,
  Group,
  CollectorGroupSettingsV2Input,
  GlobalCollectorSettingsQuery
} from 'graphql/types';
import { SettingId } from '../../types';

export type GroupsResult = Record<
  GroupType,
  { id: string; displayText: string }[]
>;
export type CollectorRowSettings = GroupsResult & {
  enableDisabled: EnabledDisabled;
};

export type DefaultRow = {
  enableDisabled: EnabledDisabled;
};

export type FormData = {
  defaultRow: DefaultRow;
  appTrackingDefaultRow: DefaultRow;
  rows: CollectorRowSettings[];
};

export interface ResultProps {
  formMethods: UseFormReturn<FormData>;
  defaultScreentimeStatus: EnabledDisabled;
  defaultAppTrackingStatus: EnabledDisabled;
}

export const mapToUiAccessSope = (
  groups: Group[]
): { id: string; displayText: string }[] => {
  return groups
    ? groups.map(({ id, name }) => ({
        id: id!,
        displayText: name!
      }))
    : [];
};

export const mapToAccessSope = (
  key: string,
  groups: any[]
):
  | {
      [x: string]: Group[];
    }
  | undefined => {
  return groups
    ? {
        [key]: groups.map(({ id, displayText }) => ({
          id: id!,
          name: displayText!
        }))
      }
    : undefined;
};

const mapToDefaultValues = (
  rows: CollectorGroupSettingsV2Input[]
): CollectorRowSettings[] =>
  rows.map(({ accessScopeIn, flags }: CollectorGroupSettingsV2Input) => {
    const {
      departments,
      employeeTypes,
      locations,
      divisions,
      costCenters,
      serviceCenters
    } = accessScopeIn || {};
    return {
      enableDisabled: flags.find((flag) => flag.key === SettingId.Enabled)
        ?.value
        ? EnabledDisabled.enabled
        : EnabledDisabled.disabled,
      [GroupType.Department]: mapToUiAccessSope(departments ?? []),
      [GroupType.EmployeeType]: mapToUiAccessSope(employeeTypes ?? []),
      [GroupType.Location]: mapToUiAccessSope(locations ?? []),
      [GroupType.Division]: mapToUiAccessSope(divisions ?? []),
      [GroupType.CostCenter]: mapToUiAccessSope(costCenters ?? []),
      [GroupType.ServiceCenter]: mapToUiAccessSope(serviceCenters ?? [])
    };
  });

export const useDesktopCollectorSettingsForm = (
  data?: GlobalCollectorSettingsQuery
): ResultProps => {
  const defaultValues = useMemo(() => {
    const globalSettings = data?.globalCollectorSettings?.items;
    const screentime = globalSettings?.find(
      (item) => item.id === SettingId.EnableScreentime
    );
    const screentimeGroupSettings = screentime?.groupSettings;
    const rows = mapToDefaultValues(screentimeGroupSettings ?? []);
    const screentimeSetting = screentime?.settings;
    const defaultScreentimeValue = screentimeSetting?.find(
      (setting) => setting.key === SettingId.EnableScreentime
    )?.value;

    const appTrackingSetting = globalSettings?.find(
      (item) => item.id === SettingId.EnableAppTracking
    )?.settings;
    const defaultapptrackingValue = appTrackingSetting?.find(
      (setting) => setting.key === SettingId.EnableAppTracking
    )?.value;

    return {
      rows,
      appTrackingDefaultRow: {
        enableDisabled: defaultapptrackingValue
          ? defaultapptrackingValue === 'true'
            ? EnabledDisabled.enabled
            : EnabledDisabled.disabled
          : EnabledDisabled.enabled
      },
      defaultRow: {
        enableDisabled: defaultScreentimeValue
          ? defaultScreentimeValue === 'true'
            ? EnabledDisabled.enabled
            : EnabledDisabled.disabled
          : EnabledDisabled.disabled
      }
    };
  }, [data]);

  const formMethods = useForm({
    defaultValues
  });

  const { getValues, reset } = formMethods;
  useEffect(() => {
    const values = getValues();
    if (defaultValues.rows) {
      reset(
        {
          ...values,
          rows: defaultValues.rows,
          defaultRow: defaultValues.defaultRow,
          appTrackingDefaultRow: defaultValues.appTrackingDefaultRow
        },
        {
          keepErrors: true,
          keepDirty: true
        }
      );
    }
  }, [
    defaultValues.defaultRow,
    defaultValues.rows,
    defaultValues.appTrackingDefaultRow,
    getValues,
    reset
  ]);
  return {
    formMethods,
    defaultScreentimeStatus: defaultValues.defaultRow.enableDisabled,
    defaultAppTrackingStatus: defaultValues.appTrackingDefaultRow.enableDisabled
  };
};
